import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from '../ui/link';
import { Button } from '../ui/atoms/Button';
import { LoginButton } from '../auth/LoginButton';
import { BookDemoButton } from '../landing/BookDemoButton';
import { LanguageGlobe } from '../i18n/LanguageGlobe';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { goToLandingSection } from '../../utils/landingNav';

export const LandingHeader: React.FC = () => {
  const { t } = useTranslation('marketing');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navItems = [
    { label: t('nav.how_it_works'), href: '#how-it-works', target: 'how_it_works' },
    { label: t('nav.pricing'), href: '#pricing', target: 'pricing' },
    { label: t('nav.blog'), href: '/blog', target: 'blog' },
  ];

  const handleNavClick = (href: string, isMobile: boolean = false) => {
    if (isMobile) setIsMobileMenuOpen(false);
    if (href.startsWith('#')) {
      goToLandingSection(href, navigate, pathname);
      return;
    }
    window.location.href = href;
  };

  const handleFreeTrialClick = (isMobile: boolean = false) => {
    if (isMobile) setIsMobileMenuOpen(false);
    navigate('/signup?plan=free_trial');
  };

  return (
    <>
      <nav className="flex justify-between items-center self-stretch relative z-[1] px-4 sm:px-6 lg:px-12 h-16 sm:h-20">
        {/* Logo */}
        <RouterLink to="/" aria-label="Go to home page" className="flex items-center gap-2">
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            whileHover={{ scale: 1.05 }}
            src="/logo/dark-logo.png"
            alt="Invirtus"
            className="w-8 h-8 lg:w-10 lg:h-10 relative"
            style={{ willChange: 'transform' }}
          />
          <span className="text-dark-invirtus text-3xl lg:text-4xl font-bold text-center">
            Invirtus
          </span>
        </RouterLink>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8 lg:gap-10 relative">
          {/* Icon-only locale switcher — first element of the nav row,
              before any nav items. Sits as a quiet utility, not a CTA. */}
          <LanguageGlobe variant="icon" align="left" />
          {navItems.map((item) => (
            <Link
              key={item.href}
              label={item.label}
              type="underline"
              intent="secondary"
              onClick={() => handleNavClick(item.href)}
              {...getGTMAttributes(createGTMTracking('NAVIGATE_SECTION', { origin: 'header', target: item.target }))}
            />
          ))}
          <div className="flex items-center gap-4">
            <BookDemoButton origin="hero" variant="outlined" />
            <Button
              label={t('nav.free_trial')}
              type="contained"
              intent="secondary"
              onClick={() => handleFreeTrialClick()}
              {...getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'header', target: 'free_trial' }))}
            />
            <LoginButton origin="header" />
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <motion.button
          className="lg:hidden text-dark-invirtus transition-colors duration-200 p-2 rounded-md hover:bg-gradient-purple/30 focus:bg-gradient-purple/30"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={t('nav.toggle_menu')}
          style={{ willChange: 'auto' }}
        >
          {isMobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          )}
        </motion.button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="lg:hidden absolute top-16 sm:top-20 border-t border-dark-invirtus-light left-0 right-0 shadow-lg z-50"
            style={{
              background: 'linear-gradient(160deg, var(--color-light) 0%, var(--color-light-lin) 30%',
              willChange: 'transform, opacity'
            }}
          >
            <div className="flex flex-col justify-center items-center py-4 px-4 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  label={item.label}
                  type="underline"
                  intent="secondary"
                  className="text-left text-base font-bold py-2"
                  onClick={() => handleNavClick(item.href, true)}
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_SECTION', { origin: 'mobile_menu', target: item.target }))}
                />
              ))}
              <div className="flex flex-col items-center gap-4 pt-2 w-full">
                <div className="flex justify-center w-full">
                  <LanguageGlobe variant="icon" align="left" />
                </div>
                <BookDemoButton origin="mobile_menu" variant="outlined" className="w-full" />
                <Button
                  label={t('nav.free_trial')}
                  type="contained"
                  intent="primary"
                  size="md"
                  className="w-full"
                  onClick={() => handleFreeTrialClick(true)}
                  {...getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'mobile_menu', target: 'free_trial' }))}
                />
                <LoginButton origin="mobile_menu" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
